import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  HostListener,
  OnDestroy,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core';
import { WebSocketService } from '@app/core/services/websocket.service';
import { WorkOrderService } from './work-order.service';
import { BadgeNotification } from '@app/core/model/badge-notification.model';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { getCurrentWorkOrder } from '@app/core/store/selectors/work-order.selector';
import { LoadCommentCountAction, ClearMarkupDocument } from '@dwo/actions';
import { Router } from '@angular/router';
import { FeatureFlagService } from '@app/core/services/feature-flag.service';
import { FeatureFlagKey } from '@dwo/enums';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import {
  getCommentCount,
  getMaterialCommentCount,
  getWorkLineCommentCount,
} from '@app/core/store/selectors/comments.selector';
import { getActiveMarkupDocumentId } from '@app/core/store/selectors/work-order.selector';
import { WeightLineCounts, WeightReportService } from './weight-report/weight-report.service';
import { UserService } from '@app/core/services/user.service';
import { ProjectControllerService } from '@app/generated/api/projectController.service';
import { WorkOrderSiteQueryService } from './site-query/site-query.service';

@Component({
  selector: 'app-work-order-navigation',
  templateUrl: './work-order-navigation.component.html',
  styleUrls: ['./work-order-navigation.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class WorkOrderNavigationComponent implements OnInit, OnDestroy, AfterViewChecked {
  workOrderId: string;
  private currentProjectId = '';

  showObjectHierarchy: boolean;

  showDocumentMenu: boolean;

  private readonly destroy$ = new Subject<void>();
  public hasComments = false;
  public hasDocuments = false;
  public materialCommentCount = 0;
  public workLineCommentCount = 0;
  public commentCount = 0;
  public weightLineCounts: WeightLineCounts = { sent: 0, total: 0 };
  public workOrderProgress = 0;
  public showWeightHandling = false;
  public showWorkOrderFeedback = false;
  public showSiteQueries = false;
  public siteQueryCount = 0;
  public showSplitView = false;
  public canUseSplitView = window.innerWidth >= WorkOrderService.MIN_SPLIT_VIEW_WIDTH;
  public activeMarkupDocumentId: string | null = null;
  private hasWeightRecipientEmail = false;
  private hasFeedbackRecipientEmail = false;

  constructor(
    private store: Store,
    private webSocketService: WebSocketService,
    private cdRef: ChangeDetectorRef,
    public workOrderService: WorkOrderService,
    private router: Router,
    private weightReportService: WeightReportService,
    private featureFlagService: FeatureFlagService,
    private userService: UserService,
    private projectController: ProjectControllerService,
    private siteQueryService: WorkOrderSiteQueryService,
    iconRegistry: MatIconRegistry,
    sanitizer: DomSanitizer,
  ) {
    iconRegistry.addSvgIcon('site-query', sanitizer.bypassSecurityTrustResourceUrl('assets/images/site-query.svg'));
  }

  toggleSplitView(): void {
    const enablingSplitView = !this.showSplitView;
    this.workOrderService.toggleSplitView();
    if (enablingSplitView && this.workOrderId) {
      this.router.navigate(['/work-order', this.workOrderId, 'objects']);
    }
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    const canSplit = this.workOrderService.canUseSplitView();
    if (canSplit === this.canUseSplitView) {
      return;
    }
    this.canUseSplitView = canSplit;
    if (!canSplit && this.showSplitView) {
      this.workOrderService.setSplitView(false);
    }
    this.cdRef.detectChanges();
  }

  onDocumentsClick(event: Event): void {
    if (this.showSplitView) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  async ngOnInit() {
    this.store
      .select(getCurrentWorkOrder)
      .pipe(takeUntil(this.destroy$))
      .subscribe((workOrder) => {
        if (workOrder) {
          if (this.workOrderId && this.workOrderId !== workOrder.id) {
            this.store.dispatch(new ClearMarkupDocument());
          }
          this.workOrderId = workOrder.id;
          this.currentProjectId = (workOrder.projectId ?? '').trim();
          this.workOrderProgress = workOrder.progress ?? 0;
          this.showDocumentMenu = false;
          this.showObjectHierarchy = true;
          this.updateRecipientMenuVisibility();
          this.resolveRecipientAvailability(this.currentProjectId);
          this.store.dispatch(new LoadCommentCountAction(this.workOrderId));
        } else {
          this.showSiteQueries = false;
          this.siteQueryService.resetCounts();
        }
      });

    this.store
      .select(getCommentCount)
      .pipe(takeUntil(this.destroy$))
      .subscribe((count) => (this.commentCount = count));
    this.weightReportService.counts$.pipe(takeUntil(this.destroy$)).subscribe((counts) => {
      this.weightLineCounts = counts;
      this.cdRef.detectChanges();
    });
    this.siteQueryService.counts$.pipe(takeUntil(this.destroy$)).subscribe((count) => {
      this.siteQueryCount = count;
      this.cdRef.detectChanges();
    });
    this.store
      .select(getMaterialCommentCount)
      .pipe(takeUntil(this.destroy$))
      .subscribe((count) => {
        this.materialCommentCount = count;
        this.cdRef.detectChanges();
      });
    this.store
      .select(getWorkLineCommentCount)
      .pipe(takeUntil(this.destroy$))
      .subscribe((count) => {
        this.workLineCommentCount = count;
        this.cdRef.detectChanges();
      });

    this.workOrderService.showSplitView$.pipe(takeUntil(this.destroy$)).subscribe((showSplitView) => {
      this.showSplitView = showSplitView;
      this.cdRef.detectChanges();
    });

    this.store
      .select(getActiveMarkupDocumentId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((id) => {
        this.activeMarkupDocumentId = id;
        this.cdRef.detectChanges();
      });

    this.webSocketService.badgeNotifications$.subscribe((notifications: BadgeNotification[]) => {
      if (notifications == null || !this.workOrderId) {
        return;
      }

      const workOrderNotification = notifications.filter(
        (notification: BadgeNotification) =>
          notification.name.endsWith('WorkOrderEntity') && notification.id === this.workOrderId,
      )[0];
      if (workOrderNotification) {
        const notificationsMap = {
          CommentEntity: 0,
          DocumentEntity: 0,
        };

        workOrderNotification.children.forEach((childNotification) => (notificationsMap[childNotification.name] += 1));

        this.hasComments = notificationsMap.CommentEntity > 0;
        this.hasDocuments = notificationsMap.DocumentEntity > 0;
      } else {
        this.hasComments = false;
        this.hasDocuments = false;
      }
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.siteQueryService.resetCounts();
  }

  // resetDocumentMenu() {
  //   this.workOrderService.navigationShowDocumentMenu = false;
  //   this.workOrderService.navigationBackRoute = '';
  // }

  clearMarkupDocument(): void {
    this.store.dispatch(new ClearMarkupDocument());
  }

  ngAfterViewChecked(): void {
    if (this.showDocumentMenu !== this.workOrderService.navigationShowDocumentMenu) {
      this.showDocumentMenu = this.workOrderService.navigationShowDocumentMenu;
      this.cdRef.detectChanges();
    }
  }

  get hasUnsentWeightLines(): boolean {
    return this.weightLineCounts.total - this.weightLineCounts.sent > 0;
  }

  /** Unsent weight lines on a finished work order need to stand out. */
  get weightBadgeAlert(): boolean {
    return this.hasUnsentWeightLines && this.workOrderProgress >= 100;
  }

  private updateRecipientMenuVisibility(): void {
    const canShowWeight =
      this.featureFlagService.isEnabled(FeatureFlagKey.EnableWeightHandling, this.currentProjectId) &&
      this.hasWeightRecipientEmail;
    const canShowFeedback =
      this.featureFlagService.isEnabled(FeatureFlagKey.EnableWorkOrderFeedback, this.currentProjectId) &&
      this.hasFeedbackRecipientEmail;
    this.showSiteQueries = this.featureFlagService.isEnabled(FeatureFlagKey.EnableSiteQuery, this.currentProjectId);
    if (this.showSiteQueries && this.workOrderId) {
      this.siteQueryService.loadCounts(this.workOrderId);
    } else {
      this.siteQueryService.resetCounts();
    }

    const previousWeightVisibility = this.showWeightHandling;
    this.showWeightHandling = canShowWeight;
    this.showWorkOrderFeedback = canShowFeedback;

    if (this.showWeightHandling && !previousWeightVisibility && this.workOrderId) {
      this.weightReportService.loadCounts(this.workOrderId);
    } else if (!this.showWeightHandling) {
      this.weightLineCounts = { sent: 0, total: 0 };
    }
  }

  private resolveRecipientAvailability(projectId: string): void {
    if (!projectId) {
      this.hasWeightRecipientEmail = false;
      this.hasFeedbackRecipientEmail = false;
      this.updateRecipientMenuVisibility();
      return;
    }

    this.userService
      .getCurrentUser()
      .pipe(takeUntil(this.destroy$))
      .subscribe((user) => {
        const username = (user?.username ?? '').toString().trim();
        if (!username) {
          this.hasWeightRecipientEmail = false;
          this.hasFeedbackRecipientEmail = false;
          this.updateRecipientMenuVisibility();
          return;
        }

        this.projectController
          .getAuthorizedProjects(username)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (projects) => {
              const project = (projects ?? []).find((p) => (p?.id ?? '').trim() === projectId);
              this.hasWeightRecipientEmail = !!project?.weightChangeRecipientEmail?.trim();
              this.hasFeedbackRecipientEmail = !!project?.jobFeedbackRecipientEmail?.trim();
              this.updateRecipientMenuVisibility();
            },
            error: () => {
              this.hasWeightRecipientEmail = false;
              this.hasFeedbackRecipientEmail = false;
              this.updateRecipientMenuVisibility();
            },
          });
      });
  }
}
